// Owns a matrix sub-item block's state: which of the chart and the word cloud is on screen and,
// when it is the chart, its mode and zoom; and the sort order. Seeded from the batch's params and
// re-seeded whenever any of them changes outside the block.
//
// The state was split across three coffee views -- the block owned the cloud marker, the chart view
// the mode and the zoom, the items list the sort order. It is one bag here, and the block answers
// the exporters for all of it, because the sub-item view hosting the block reaches these bridges
// through it.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getModelValue, getTrait } from '@aytm/app/stats/lib/stat-page'
import {
  isRType as calcIsRType,
  isTraitType as calcIsTraitType,
  isWCType as calcIsWCType
} from '@aytm/app/stats/page/components/lib/utils'
import { calcDisplayRange } from '@aytm/app/stats/page/question-views/oe/lib/display-range'
import {
  calcChartParamsSection,
  calcDefaultChartMode,
  calcModelChartMode,
  calcSortParamsSection,
  isWordCloud as calcIsWordCloud,
  resolveChartMode,
  resolveSortOrder,
  WC_CHART_MODE
} from '@aytm/app/stats/page/question-views/mx/components/items/lib/report-params'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { decodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

export default function useSubItemMatrixState({
  params,
  paramsRevision,
  questionKey,
  questionType,
  questionSubType,
  format,
  answer,
  answerVariants,
  defaultSortOrder,
  onReady,
  onStateChange
}) {
  const context = { key: questionKey, questionType, questionSubType, format, answer }

  const contextRef = useRef()
  contextRef.current = { ...context, defaultSortOrder, answerVariants }

  const seed = useCallback(source => {
    const { format, defaultSortOrder } = contextRef.current

    return {
      chartMode: resolveChartMode(source, format),
      zoomLevel: decodeZoomParam(source.z),
      sortOrder: resolveSortOrder(source.s, defaultSortOrder)
    }
    // everything it reads comes off the ref, so it never re-binds
  }, [])

  const [state, setState] = useState(() => seed(params || {}))

  const snapshot = useRef()
  snapshot.current = { state, context }

  // the mounted chart knows the axis it is showing; the exporters ask the state for it
  const chartApiRef = useRef(null)
  const onChartReady = useCallback(api => {
    chartApiRef.current = api
  }, [])

  // the cloud, when it is the one showing, hands its api up the same way the chart does
  const wcApiRef = useRef(null)
  const onWcReady = useCallback(api => {
    wcApiRef.current = api
  }, [])

  const pendingReason = useRef('seed')

  const onModelChange = useCallback(
    (change = {}) =>
      setState(current => {
        if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

        pendingReason.current = 'state'

        return { ...current, ...change }
      }),
    []
  )

  const api = useMemo(() => {
    // the mode the chart opens on once the question and global defaults are applied -- what the
    // exporters draw by, as opposed to `chartMode`, which is null until the researcher picks one
    const resolveExportChartMode = () => {
      const { state, context } = snapshot.current

      return calcDefaultChartMode({
        ...context,
        chartMode: state.chartMode,
        questionChartMode: calcModelChartMode(state.chartMode, context.format),
        globalChartMode: getModelValue('chartMode')
      })
    }

    // the axis the chart is showing. The rendered chart knows it, zoom included; the maths is the
    // fallback for a chart that has not drawn yet.
    const resolveDisplayRange = (chartMode, zoomLevel) => {
      const { state, context } = snapshot.current
      if (getTrait() && calcIsTraitType(context)) return null

      const extremes = chartApiRef.current?.getAxisExtremes?.()
      if (null != extremes) return extremes

      return calcDisplayRange({
        chartMode,
        zoomLevel: zoomLevel || state.zoomLevel || getModelValue('zoomLevel'),
        globalChartMode: getModelValue('chartMode'),
        format: context.format,
        answer: context.answer,
        answerVariants: contextRef.current.answerVariants,
        rType: calcIsRType(context) ? getModelValue('rType') : 'cases',
        confidenceLevels: PageGlobals.CONFIDENCE_LEVELS
      })
    }

    return {
      getState: () => ({ ...snapshot.current.state }),
      // the page walks the batches to find the ones worth pouring a preset into, and asks each
      // whether it could show a cloud at all -- not whether it is showing one
      isWCType: () =>
        calcIsWCType({
          questionType: snapshot.current.context.questionType,
          answerVariants: contextRef.current.answerVariants
        }),
      getReportParamsSection: () => {
        const { state, context } = snapshot.current

        return {
          ...calcChartParamsSection({ ...context, chartMode: state.chartMode }),
          ...calcSortParamsSection(state.sortOrder, contextRef.current.defaultSortOrder)
        }
      },
      // pptx / summary bridge: what the batch is showing
      getExportData: () => {
        const { state } = snapshot.current
        const chartModeResolved = resolveExportChartMode()

        return {
          chartMode: state.chartMode,
          chartModeResolved,
          displayRange: resolveDisplayRange(chartModeResolved, null),
          sortOrder: state.sortOrder
        }
      },
      getDisplayRange: resolveDisplayRange,
      // preset bridge: the param the batch's cloud would end up with if the preset were poured into
      // it. Resolving commits nothing; the shell writes the section down.
      getWCParam: preset => {
        // the api outlives the cloud being taken off screen, so what is showing decides, not the ref
        const wcApi = calcIsWordCloud(snapshot.current.state.chartMode) ? wcApiRef.current : null

        return wcApi ? { ...wcApi.getWcParamsSection?.(wcApi.importWcPreset?.(preset)) } : {}
      },
      update: onModelChange
    }
  }, [onModelChange])

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // A param moved outside the block (another view of the question, the URL, an undo, a report
  // revert): re-seed from it.
  //
  // Keyed on the revision the host bumps per render, not on the values inside the params bag. The
  // host reads every batch's params off the live model, so a host-driven render is a fresh read,
  // while a re-render React starts for itself carries the revision it already had. Keying on the
  // values missed a revert of a value the batch had already moved away from locally: the batch's
  // own write is silent, so the values it was handed had never changed and there was nothing to
  // compare.
  useEffect(() => {
    pendingReason.current = 'seed'
    setState(seed(params ?? {}))
  }, [params, paramsRevision, seed])

  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    if (reason) onStateChangeRef.current?.(reason)
  }, [state])

  // The strip offers one switch, not a mode: leaving the cloud hands the batch back whatever chart
  // mode it would have shown anyway.
  const onWcToggle = useCallback(
    () => onModelChange({ chartMode: calcIsWordCloud(snapshot.current.state.chartMode) ? null : WC_CHART_MODE }),
    [onModelChange]
  )

  const onChartModeChange = useCallback(chartMode => onModelChange({ chartMode }), [onModelChange])

  const onZoomChange = useCallback((key, zoomLevel) => onModelChange({ zoomLevel }), [onModelChange])

  const onSortChange = useCallback(sortOrder => onModelChange({ sortOrder }), [onModelChange])

  return {
    ...state,
    // the batch only draws a chart when the cloud is not up, so `chartMode` reaches it as the mode
    // it settled on and never as the marker
    modelChartMode: calcModelChartMode(state.chartMode, format),
    isWordCloud: calcIsWordCloud(state.chartMode),
    onChartReady,
    onChartModeChange,
    onZoomChange,
    onSortChange,
    onWcReady,
    onWcToggle,
    onModelChange
  }
}
